const EMBED_PARAM = 'pardes_form_embed';
const TFA_NEXT = 'tfa_next';
const MSG_READY = 'pardes-form-embed-ready';
const MSG_LEAVE = 'pardes-form-embed-leave';

export function recoverEmbedSrc(currentHref, originalSrc) {
	try {
		const current = new URL(currentHref);
		const original = new URL(originalSrc);

		if (current.origin !== original.origin || current.searchParams.has(EMBED_PARAM)) {
			return null;
		}

		const tfaNext = current.searchParams.get(TFA_NEXT);
		if (tfaNext) {
			original.searchParams.set(TFA_NEXT, tfaNext);
		}

		return original.href === current.href ? null : original.href;
	} catch {
		return null;
	}
}

const WARMUP_DELAY_MS = 300;
const WARMUP_COOLDOWN_MS = 1500;

export function bindFormEmbedWarmup(card, src, formIframe, isPopupOpen = () => false) {
	if (!src || !formIframe) return;

	let timer = null;
	const start = () => {
		clearTimeout(timer);
		timer = setTimeout(() => {
			if (isPopupOpen()) return;
			formIframe.warm(src);
		}, WARMUP_DELAY_MS);
	};
	const cancel = () => clearTimeout(timer);

	card.addEventListener('pointerenter', start);
	card.addEventListener('pointerleave', cancel);
	card.addEventListener('focus', start);
	card.addEventListener('blur', cancel);
}

export default class FormIframe {
	constructor(wrap, { className, title = 'Form' }) {
		this.wrap = wrap;
		this.className = className;
		this.title = title;
		this.lastTeardownAt = -Infinity;
		this.resetState();
	}

	resetState() {
		this.iframe = null;
		this.observer = null;
		this.onResize = null;
		this.onMessage = null;
		this.src = '';
		this.recovered = false;
		this.leaveBound = null;
	}

	warm(src) {
		if (performance.now() - this.lastTeardownAt < WARMUP_COOLDOWN_MS) return;
		this.mount(src);
	}

	isMounted(src) {
		return !!this.iframe && this.iframe.isConnected && this.src === src && !this.recovered;
	}

	mount(src) {
		if (!this.wrap) return;
		if (this.isMounted(src)) return;

		this.teardown();
		this.src = src;
		this.setVisible(false);

		this.iframe = document.createElement('iframe');
		this.iframe.className = this.className;
		this.iframe.setAttribute('title', this.title);
		this.iframe.setAttribute('scrolling', 'no');
		this.iframe.src = src;
		this.iframe.addEventListener('load', () => this.onLoad());

		this.onMessage = (event) => this.onEmbedMessage(event);
		window.addEventListener('message', this.onMessage);
		this.wrap.appendChild(this.iframe);

		this.onResize = () => this.resize();
		window.addEventListener('resize', this.onResize);
	}

	onEmbedMessage({ origin, source, data }) {
		if (origin !== window.location.origin || source !== this.iframe?.contentWindow) return;
		if (data?.type === MSG_LEAVE) this.setVisible(false);
		if (data?.type === MSG_READY) this.reveal();
	}

	onLoad() {
		if (this.isEmbedPage()) {
			this.reveal();
			return;
		}

		this.setVisible(false);
		if (this.recovered) return;

		const next = this.recoverSrc();
		if (!next) return;

		this.recovered = true;
		this.iframe.src = next;
	}

	reveal() {
		this.setVisible(true);
		this.bindLeave();
		this.resize();
	}

	isEmbedPage() {
		try {
			return this.iframe.contentDocument?.body?.classList.contains('form-embed-page') ?? false;
		} catch {
			return false;
		}
	}

	bindLeave() {
		try {
			const win = this.iframe?.contentWindow;
			const doc = this.iframe?.contentDocument;
			if (!win || this.leaveBound === win) return;

			this.leaveBound = win;
			const hide = () => this.setVisible(false);
			win.addEventListener('pagehide', hide);
			win.addEventListener('beforeunload', hide);
			doc?.querySelectorAll('form').forEach((form) => {
				form.addEventListener('submit', hide, true);
			});
		} catch {
			return;
		}
	}

	setVisible(visible) {
		if (!this.wrap) return;
		this.wrap.classList.toggle('is-embed-ready', visible);
		this.wrap.classList.toggle('is-loading', !visible);
	}

	recoverSrc() {
		try {
			return recoverEmbedSrc(this.iframe.contentWindow?.location?.href, this.src);
		} catch {
			return null;
		}
	}

	resize() {
		try {
			const doc = this.iframe?.contentDocument;
			if (!doc?.body) return;

			const measure = () => Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
			this.iframe.style.height = `${measure()}px`;

			this.observer?.disconnect();
			this.observer = new MutationObserver(() => {
				const h = measure();
				if (Math.abs(h - this.iframe.offsetHeight) > 2) {
					this.iframe.style.height = `${h}px`;
				}
			});
			this.observer.observe(doc.body, { childList: true, subtree: true, attributes: true });
		} catch {
			return;
		}
	}

	teardown() {
		if (this.iframe) this.lastTeardownAt = performance.now();
		if (this.onResize) window.removeEventListener('resize', this.onResize);
		if (this.onMessage) window.removeEventListener('message', this.onMessage);
		this.observer?.disconnect();

		if (this.wrap) {
			this.wrap.innerHTML = '';
			this.wrap.classList.remove('is-loading', 'is-embed-ready');
		}

		this.resetState();
	}
}
